import React, { useState, useMemo } from 'react';
import { WsSessionChatMessage } from '../../services/socket.service';

export interface PiPHelpRequest {
  helpRequestId: string;
  studentName: string;
  stepTitle?: string;
  stepOrderIndex?: number;
  message: string;
  status: 'new' | 'replied';
  lastReply?: string;
  createdAt: number;
}

export interface PiPReaction {
  emoji: string;
  count: number;
  names: string[];
}

interface PiPWidgetProps {
  steps: { id: string; title: string; orderIndex: number }[];
  stepData: Map<string, { completed: string[]; inProgress: string[] }>;
  totalStudents: number;
  studentPositions: Map<string, { name: string; highestCompletedIndex: number }>;
  instructorHWM: number;
  reactions: PiPReaction[];
  chatMessages: WsSessionChatMessage[];
  chatUnreadCount: number;
  onSendChatMessage: (message: string) => void;
  onChatViewed: () => void;
  currentUserId: string;
  onClose: () => void;
}

// ─── Color Palette ───
const C = {
  bg: '#08080f',
  panel: '#0e0e1a',
  cell: '#161625',
  cellHover: '#1e1e32',
  border: '#1f1f35',
  borderHi: '#2d2d4a',
  text: '#e4e4f0',
  text2: '#9898b4',
  text3: '#5c5c78',
  cyan: '#22d3ee',
  cyan20: 'rgba(34, 211, 238, 0.15)',
  cyan40: 'rgba(34, 211, 238, 0.35)',
  orange: '#fb923c',
  orangeBg: 'rgba(251, 146, 60, 0.08)',
  orange20: 'rgba(251, 146, 60, 0.18)',
  red: '#f87171',
  green: '#4ade80',
  greenDim: 'rgba(74, 222, 128, 0.25)',
  blue: '#60a5fa',
  blueBg: 'rgba(96, 165, 250, 0.12)',
};

const FONT = "'Lexend', -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', monospace";

const PiPWidget: React.FC<PiPWidgetProps> = ({
  steps,
  stepData,
  totalStudents,
  studentPositions,
  instructorHWM,
  reactions,
  chatMessages,
  chatUnreadCount,
  onSendChatMessage,
  onChatViewed,
  currentUserId,
  onClose,
}) => {
  const sortedSteps = useMemo(() => [...steps].sort((a, b) => a.orderIndex - b.orderIndex), [steps]);

  // ─── Behind Calculation ───
  const behindStudents = useMemo(() => {
    const behind: { name: string; stepsBehind: number; currentStep: number }[] = [];
    Array.from(studentPositions.values()).forEach(pos => {
      if (pos.highestCompletedIndex < instructorHWM - 1) {
        behind.push({
          name: pos.name,
          stepsBehind: instructorHWM - pos.highestCompletedIndex - 1,
          currentStep: pos.highestCompletedIndex + 2,
        });
      }
    });
    return behind.sort((a, b) => b.stepsBehind - a.stepsBehind);
  }, [studentPositions, instructorHWM]);

  // ─── Overall completion stats ───
  const overallStats = useMemo(() => {
    if (sortedSteps.length === 0 || totalStudents === 0) return { text: '0/0 complete' };
    let totalCompleted = 0;
    for (const step of sortedSteps) {
      const d = stepData.get(step.id);
      if (d) totalCompleted += d.completed.length;
    }
    const pct = Math.round((totalCompleted / (sortedSteps.length * totalStudents)) * 100);
    if (sortedSteps.length <= 8) {
      const stepsFullyDone = sortedSteps.filter(s => (stepData.get(s.id)?.completed.length || 0) >= totalStudents).length;
      return { text: `${stepsFullyDone}/${sortedSteps.length} complete` };
    }
    return { text: `${pct}% overall` };
  }, [sortedSteps, stepData, totalStudents]);

  // ─── Chat state ───
  const [chatInput, setChatInput] = useState('');
  const [hoveredSeg, setHoveredSeg] = useState<number | null>(null);
  const [behindHover, setBehindHover] = useState(false);
  const [hoveredReaction, setHoveredReaction] = useState<string | null>(null);
  const [chatHover, setChatHover] = useState(false);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'grid',
        gridTemplateRows: '26px 42px 28px 20px minmax(0, 1fr)',
        background: C.panel,
        overflow: 'hidden',
        position: 'relative',
        fontFamily: FONT,
      }}
    >
      {/* ═══ ROW 1: HEADER (26px) ═══ */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 10px',
          borderBottom: `1px solid ${C.border}`,
          gap: 5,
        }}
      >
        <div style={{
          width: 5, height: 5, borderRadius: '50%',
          background: C.green,
          boxShadow: `0 0 4px ${C.green}`,
          animation: 'livePulse 2s infinite',
        }} />
        <span style={{ fontSize: 9.5, fontWeight: 600, color: C.text, letterSpacing: 0.4, flex: 1 }}>
          Praxis Live
        </span>
        <span style={{ fontSize: 8.5, fontFamily: MONO, color: C.text3 }}>
          {totalStudents} joined
        </span>
      </div>

      {/* ═══ ROW 2: STEP HEATMAP (42px) ═══ */}
      <div style={{ padding: '6px 10px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4, position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 8, fontWeight: 500, letterSpacing: 0.6, textTransform: 'uppercase', color: C.text3 }}>Steps</span>
          <span style={{ fontSize: 9, fontFamily: MONO, fontWeight: 500, color: C.cyan }}>{overallStats.text}</span>
        </div>
        <div style={{ display: 'flex', gap: 1, height: 16, borderRadius: 3, overflow: 'visible', position: 'relative' }}>
          {sortedSteps.map((step, idx) => {
            const d = stepData.get(step.id);
            const completedCount = d?.completed.length || 0;
            const pct = totalStudents > 0 ? completedCount / totalStudents : 0;
            let segColor: string;
            if (pct >= 1) segColor = C.greenDim;
            else if (pct >= 0.6) segColor = C.cyan40;
            else if (pct >= 0.3) segColor = C.cyan20;
            else if (pct > 0) segColor = 'rgba(251, 146, 60, 0.2)';
            else segColor = C.cell;

            const isHovered = hoveredSeg === idx;

            const behindOnStep = instructorHWM > 0 && step.orderIndex <= instructorHWM
              ? Array.from(studentPositions.values()).filter(p => p.highestCompletedIndex < step.orderIndex).map(p => p.name)
              : [];

            return (
              <div
                key={step.id}
                style={{
                  flex: 1,
                  minWidth: 2,
                  borderRadius: 2,
                  background: segColor,
                  position: 'relative',
                  cursor: 'default',
                  transition: 'transform 0.1s, box-shadow 0.1s',
                  transform: isHovered ? 'scaleY(1.4) scaleX(1.1)' : undefined,
                  zIndex: isHovered ? 10 : undefined,
                  boxShadow: isHovered ? '0 0 6px rgba(34, 211, 238, 0.3)' : undefined,
                }}
                onMouseEnter={() => setHoveredSeg(idx)}
                onMouseLeave={() => setHoveredSeg(null)}
              >
                {isHovered && (
                  <div style={{
                    position: 'absolute',
                    bottom: 'calc(100% + 8px)',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: C.cell,
                    border: `1px solid ${C.borderHi}`,
                    borderRadius: 6,
                    padding: '6px 9px',
                    minWidth: 140,
                    maxWidth: 180,
                    zIndex: 1000,
                    boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
                    pointerEvents: 'none',
                  }}>
                    <div style={{ fontSize: 9, fontWeight: 600, color: C.text, marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {step.orderIndex + 1} · {step.title}
                    </div>
                    {behindOnStep.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 1 }}>
                        <span style={{ fontSize: 7.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4, color: C.text3, flexShrink: 0 }}>Behind:</span>
                        <span style={{ fontSize: 8.5, color: C.orange, lineHeight: '1.3' }}>
                          {behindOnStep.length <= 3 ? behindOnStep.join(', ') : `${behindOnStep.length} students`}
                        </span>
                      </div>
                    )}
                    {(d?.inProgress.length || 0) > 0 && (
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 1 }}>
                        <span style={{ fontSize: 7.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4, color: C.text3, flexShrink: 0 }}>Working:</span>
                        <span style={{ fontSize: 8.5, color: C.cyan, lineHeight: '1.3' }}>
                          {d!.inProgress.length <= 3 ? d!.inProgress.join(', ') : `${d!.inProgress.length} students`}
                        </span>
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <span style={{ fontSize: 7.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4, color: C.text3, flexShrink: 0 }}>Done:</span>
                      <span style={{ fontSize: 8.5, color: C.text2, lineHeight: '1.3' }}>
                        {completedCount === 0 ? 'Not started' :
                         completedCount >= totalStudents ? `All ${totalStudents} ✓` :
                         completedCount <= 5 ? d!.completed.join(', ') : `${completedCount}/${totalStudents}`}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ═══ ROW 3: BEHIND ALERT (28px) ═══ */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 10px',
          gap: 6,
          background: behindStudents.length > 0 ? C.orangeBg : 'transparent',
          borderTop: `1px solid ${behindStudents.length > 0 ? 'rgba(251, 146, 60, 0.08)' : C.border}`,
          borderBottom: `1px solid ${behindStudents.length > 0 ? 'rgba(251, 146, 60, 0.08)' : C.border}`,
          position: 'relative',
          cursor: 'default',
        }}
        onMouseEnter={() => setBehindHover(true)}
        onMouseLeave={() => setBehindHover(false)}
      >
        <div style={{
          width: behindStudents.length > 0 ? 7 : 5,
          height: behindStudents.length > 0 ? 7 : 5,
          borderRadius: '50%',
          background: behindStudents.length > 0 ? C.orange : C.green,
          flexShrink: 0,
          animation: behindStudents.length > 0 ? 'beaconPulse 1.4s infinite' : undefined,
        }} />
        <span style={{
          fontSize: 9,
          fontWeight: behindStudents.length > 0 ? 500 : 400,
          color: behindStudents.length > 0 ? C.orange : C.text3,
          flex: 1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {behindStudents.length === 0 ? 'All on track' : (
            `${behindStudents.length} behind — ${behindStudents.slice(0, 2).map(s => s.name.split(' ')[0]).join(', ')}${behindStudents.length > 2 ? ` +${behindStudents.length - 2}` : ''}`
          )}
        </span>
        {behindStudents.length > 0 && (
          <span style={{ fontSize: 8, fontFamily: MONO, color: C.text3, flexShrink: 0 }}>hover</span>
        )}
        {behindHover && behindStudents.length > 0 && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 8,
            right: 8,
            background: C.cell,
            border: `1px solid ${C.borderHi}`,
            borderRadius: 6,
            padding: '6px 8px',
            zIndex: 1000,
            boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
            maxHeight: 120,
            overflowY: 'auto',
          }}>
            {behindStudents.map((s, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '2px 0',
                borderBottom: i < behindStudents.length - 1 ? `1px solid ${C.border}` : undefined,
              }}>
                <span style={{ fontSize: 9, fontWeight: 500, color: C.text }}>{s.name}</span>
                <span style={{ fontSize: 8, fontFamily: MONO, color: C.orange }}>step {s.currentStep}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ═══ ROW 4: REACTIONS (20px) ═══ */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 10px',
          gap: 4,
          borderTop: `1px solid ${C.border}`,
          position: 'relative',
          overflow: 'visible',
        }}
      >
        {reactions.map((r) => {
          const isHovered = hoveredReaction === r.emoji;
          const tooltipText = r.count <= 5
            ? r.names.join(', ')
            : `${r.count} students`;
          return (
            <div
              key={r.emoji}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                background: C.cell,
                borderRadius: 3,
                padding: '2px 5px',
                cursor: 'default',
                position: 'relative',
              }}
              onMouseEnter={() => setHoveredReaction(r.emoji)}
              onMouseLeave={() => setHoveredReaction(null)}
            >
              <span style={{ fontSize: 9, lineHeight: 1 }}>{r.emoji}</span>
              <span style={{ fontSize: 8, fontFamily: MONO, color: C.text3, lineHeight: 1 }}>{r.count}</span>
              {isHovered && (
                <div style={{
                  position: 'absolute',
                  bottom: 'calc(100% + 4px)',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: C.cell,
                  border: `1px solid ${C.borderHi}`,
                  borderRadius: 4,
                  padding: '3px 6px',
                  zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                }}>
                  <span style={{ fontSize: 8, color: C.text2 }}>{tooltipText}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ═══ ROW 5: SESSION CHAT ═══ */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          borderTop: `1px solid ${C.border}`,
          position: 'relative',
          overflow: 'hidden',
          minHeight: 0,
        }}
        onMouseEnter={() => setChatHover(true)}
        onMouseLeave={() => setChatHover(false)}
      >
        {/* Chat Header — 18px */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px',
          height: 18,
          minHeight: 18,
          borderBottom: `1px solid ${C.border}`,
          gap: 4,
        }}>
          <span style={{ fontSize: 8, lineHeight: 1 }}>&#9648;</span>
          <span style={{
            fontSize: 7.5,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: C.text3,
            flex: 1,
          }}>Session Chat</span>
          {chatUnreadCount > 0 && (
            <span style={{
              fontSize: 7,
              fontFamily: MONO,
              fontWeight: 600,
              background: C.cyan,
              color: C.bg,
              minWidth: 14,
              height: 12,
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 3px',
              animation: 'unreadPulse 2.5s infinite',
            }}>{chatUnreadCount}</span>
          )}
        </div>

        {/* Message Feed — fills remaining space, shows all messages */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            padding: '3px 8px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
          onMouseEnter={onChatViewed}
          ref={(el) => { if (el) el.scrollTop = el.scrollHeight; }}
        >
          {chatMessages.map((msg, i) => (
            <div key={msg.timestamp + '-' + i} style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              padding: '2px 4px',
              borderRadius: 3,
              borderLeft: `2px solid ${msg.userRole === 'instructor' ? C.cyan : C.borderHi}`,
              background: msg.userRole === 'instructor' ? 'rgba(34,211,238,0.06)' : 'transparent',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{
                  fontSize: 7.5,
                  fontWeight: 600,
                  color: msg.userRole === 'instructor' ? C.cyan : C.orange,
                  whiteSpace: 'nowrap',
                }}>
                  {msg.userId === currentUserId ? 'You' : msg.userName.split(' ')[0]}
                </span>
                {msg.userRole === 'instructor' && (
                  <span style={{
                    fontSize: 6,
                    fontFamily: MONO,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                    color: C.bg,
                    background: C.cyan,
                    padding: '0 3px',
                    borderRadius: 2,
                    lineHeight: '1.5',
                  }}>HOST</span>
                )}
              </div>
              <div style={{
                fontSize: 8,
                color: C.text,
                lineHeight: '1.3',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 220,
              }}>
                {msg.message}
              </div>
            </div>
          ))}
          {chatMessages.length === 0 && (
            <span style={{ fontSize: 8, color: C.text3, fontStyle: 'italic', textAlign: 'center', padding: '8px 0' }}>
              No messages yet
            </span>
          )}
        </div>

        {/* Input Bar — 24px */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          height: 24,
          minHeight: 24,
          padding: '0 6px',
          gap: 4,
          borderTop: `1px solid ${C.border}`,
          background: 'rgba(0,0,0,0.15)',
        }}>
          <input
            type="text"
            placeholder="Message the session..."
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && chatInput.trim()) {
                onSendChatMessage(chatInput.trim());
                setChatInput('');
              }
            }}
            style={{
              flex: 1,
              background: C.cell,
              border: `1px solid ${C.border}`,
              borderRadius: 3,
              padding: '2px 6px',
              fontFamily: FONT,
              fontSize: 8,
              color: C.text,
              outline: 'none',
              height: 18,
            }}
          />
          <button
            onClick={() => {
              if (chatInput.trim()) {
                onSendChatMessage(chatInput.trim());
                setChatInput('');
              }
            }}
            style={{
              width: 18,
              height: 18,
              borderRadius: 3,
              border: 'none',
              background: chatInput.trim() ? C.cyan : C.cyan20,
              color: chatInput.trim() ? C.bg : C.cyan,
              fontSize: 9,
              cursor: chatInput.trim() ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.1s',
              flexShrink: 0,
            }}
          >&#9654;</button>
        </div>

        {/* Chat history hover popup — overlays inside the widget */}
        {chatHover && chatMessages.length > 2 && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: C.cell,
            padding: 8,
            zIndex: 1000,
            overflowY: 'auto',
          }}>
            <div style={{
              fontSize: 8,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: C.text3,
              marginBottom: 6,
              paddingBottom: 4,
              borderBottom: `1px solid ${C.border}`,
            }}>
              Chat History — {chatMessages.length} messages
            </div>
            {chatMessages.map((msg, i) => (
              <div key={msg.timestamp + '-popup-' + i} style={{
                padding: '3px 5px',
                borderRadius: 3,
                borderLeft: `2px solid ${msg.userRole === 'instructor' ? C.cyan : C.borderHi}`,
                background: msg.userRole === 'instructor' ? 'rgba(34,211,238,0.06)' : 'transparent',
                marginBottom: 3,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{
                    fontSize: 7.5,
                    fontWeight: 600,
                    color: msg.userRole === 'instructor' ? C.cyan : C.orange,
                    whiteSpace: 'nowrap',
                  }}>
                    {msg.userId === currentUserId ? 'You' : msg.userName.split(' ')[0]}
                  </span>
                  {msg.userRole === 'instructor' && (
                    <span style={{
                      fontSize: 6,
                      fontFamily: MONO,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                      color: C.bg,
                      background: C.cyan,
                      padding: '0 3px',
                      borderRadius: 2,
                      lineHeight: '1.5',
                    }}>HOST</span>
                  )}
                </div>
                <div style={{
                  fontSize: 8,
                  color: C.text,
                  lineHeight: '1.3',
                  whiteSpace: 'normal',
                  overflow: 'visible',
                }}>
                  {msg.message}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ═══ CSS KEYFRAMES ═══ */}
      <style>{`
        @keyframes livePulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 4px ${C.green}; }
          50% { opacity: 0.5; box-shadow: 0 0 1px ${C.green}; }
        }
        @keyframes beaconPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.85); }
        }
        @keyframes unreadPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(34, 211, 238, 0.3); }
          50% { box-shadow: 0 0 0 2px rgba(34, 211, 238, 0.1); }
        }
      `}</style>
    </div>
  );
};

export default PiPWidget;
